import React from 'react';

const AuthContext = React.createContext(null);

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8003').replace(/\/$/, '');

export function AuthProvider({ children }) {
  const [token, setToken] = React.useState(() => window.localStorage.getItem('token') || '');
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const clearAuth = React.useCallback(() => {
    setToken('');
    setUser(null);
    window.localStorage.removeItem('token');
  }, []);

  const validateToken = React.useCallback(
    async (tokenToValidate) => {
      if (!tokenToValidate) {
        clearAuth();
        return false;
      }
      try {
        const res = await fetch(`${API_BASE}/api/auth/validate-token`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokenToValidate}`,
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.user) {
          clearAuth();
          return false;
        }
        setToken(tokenToValidate);
        setUser(data.user);
        window.localStorage.setItem('token', tokenToValidate);
        return true;
      } catch (_e) {
        clearAuth();
        return false;
      }
    },
    [clearAuth],
  );

  React.useEffect(() => {
    validateToken(token).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = React.useCallback(
    async (email, password) => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.token || !data?.user) {
          return { success: false, error: data?.error || 'Bejelentkezés sikertelen.' };
        }
        setToken(data.token);
        setUser(data.user);
        window.localStorage.setItem('token', data.token);
        return { success: true };
      } catch (e) {
        return { success: false, error: e.message || 'Bejelentkezési hiba.' };
      }
    },
    [],
  );

  const loginWithGoogle = React.useCallback(async (credential) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.token || !data?.user) {
        return { success: false, error: data?.error || 'Google bejelentkezés sikertelen.' };
      }
      setToken(data.token);
      setUser(data.user);
      window.localStorage.setItem('token', data.token);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message || 'Google bejelentkezési hiba.' };
    }
  }, []);

  const loginWithGoogleCode = React.useCallback(async (code) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/google/code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.token || !data?.user) {
        return { success: false, error: data?.error || 'Google bejelentkezés sikertelen.' };
      }
      setToken(data.token);
      setUser(data.user);
      window.localStorage.setItem('token', data.token);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message || 'Google bejelentkezési hiba.' };
    }
  }, []);

  const register = React.useCallback(async (username, email, password, referralCode = '') => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, referralCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.token || !data?.user) {
        return { success: false, error: data?.error || 'Regisztráció sikertelen.' };
      }
      setToken(data.token);
      setUser(data.user);
      window.localStorage.setItem('token', data.token);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message || 'Regisztrációs hiba.' };
    }
  }, []);

  const logout = React.useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const value = React.useMemo(
    () => ({
      token,
      user,
      loading,
      isAuthenticated: !!token && !!user,
      login,
      loginWithGoogle,
      loginWithGoogleCode,
      register,
      validateToken,
      logout,
    }),
    [token, user, loading, login, loginWithGoogle, loginWithGoogleCode, register, validateToken, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
